import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, Lock, Play, CheckCircle, Clock, ChevronRight, ChevronDown, Video, FileText, X, Apple, Leaf, ShieldAlert, Factory, Check } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useActivityTracker } from '../hooks/useActivityTracker';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Symptom tracking categories
const symptomCategories = [
  'Constipation', 'Energy levels', 'Headaches', 'Bloating', 'Skin clarity',
  'Sleep quality', 'Mood stability', 'Cravings', 'Joint pain', 'Mental clarity',
  'Digestion', 'Inflammation',
];

// Conquering Cravings — tick foods you crave each week
const cravingItems = [
  'Alcohol', 'Bread', 'Cakes', 'Cheese', 'Chocolate', 'Chips', 'Cigarettes',
  'Coffee/tea', 'Fatty foods', 'Fried foods', 'Fruit', 'Junk foods',
  'Orange juice', 'Pasta', 'Potatoes', 'Red meat', 'Sugar', 'Water',
];
const cravingWeeks = [
  { key: 'beginW1', label: 'Beginning Week 1' },
  { key: 'endW1', label: 'End Week 1' },
  { key: 'endW2', label: 'End Week 2' },
  { key: 'endW3', label: 'End Week 3' },
];

// Food Charts — Go Ahead / Pass It Up
const foodChartGroups = [
  {
    category: 'Primary Proteins / Fats',
    goAhead: "Bass, beef, bison, buffalo, chicken, clams, crayfish, duck, eggs with yolks, elk, emu, game hens, goose, grouse, halibut, lamb, lobster, mackerel, mahi mahi, mussels, mutton, organ meats, oysters, pork, rabbit, red snapper, salmon, sardines, scallop, tuna, trout, turkey, veal, venison, wild game. All Bermuda local fishes are great choices.",
    passUp: "Lunch meats, hot dogs, any other processed meat, shark, swordfish, orange roughy (these fish are high in mercury), most protein powders.",
  },
  {
    category: 'Fats & Oils',
    goAhead: "Avocado, avocado oil (cold pressed), bacon fat, chicken fat, coconut butter, coconut milk (full fat), coconut oil, duck fat, flax oil (cold pressed), ghee (clarified butter — can buy, but I just melt butter and use the clarified portion), lard, olives, olive oil (cold pressed), palm oil, tallow, sesame oil (cold pressed).",
    passUp: "Canola oil, cottonseed oil, Crisco, hydrogenated oils, imitation butters (if it's not butter, then what the heck is it?), shortening, soybean oil, vegetable oil.",
  },
  {
    category: 'Nuts & Seeds (Raw, unsalted)',
    goAhead: "Almonds, almond butter, brazil nuts, cashews, cashew butter, chestnuts, flax seeds, hazelnuts, macadamia nuts, peanuts, pecans, pine nuts, pistachios, pumpkin seeds, sesame seeds, sunflower seeds, walnuts.",
    goAheadNote: "Nuts should preferably be raw, soaked and dried. You can add your own toastedness for flavour.",
    passUp: "Roasted, salted, or sugar-coated nuts and seeds.",
    passUpNote: "If you have allergic reactions to nuts, please continue to avoid them.",
  },
  {
    category: 'Dairy',
    categoryNote: 'Avoid until after the 8th day',
    goAhead: "Raw butter, raw cheeses, raw cultured dairy products (kefir, yogurt), raw milk.",
    goAheadNote: "Since we do not have raw milk in Bermuda, I would limit as much as possible. There are raw cheeses at Supermart; goat cheese is also an option, especially local. Kerrygold butter is great (not dairy free, but you can clarify to remove milk solids).",
    passUp: "All other organic or commercial dairy products, including butter.",
  },
  {
    category: 'Veggies',
    categoryNote: 'Consume starchy vegetables in smaller amounts',
    goAhead: "Low / Medium-Starchy Vegetables (including but not limited to): Artichoke, asparagus, beet greens, broccoli, brussels sprouts, cabbage, carrots, cauliflower, cilantro, collards, cucumber, dandelion, eggplant, endive, green onions, kale, kohlrabi, lettuce, mushrooms, mustard greens, onions, parsley, peppers, radish, rutabaga, sea vegetables, spinach, swiss chard, tomatillos, tomato, turnip greens, turnips, yellow squash, watercress, zucchini.\n\nStarchy Vegetables*: Beet, parsnip, pumpkin, sweet potatoes, winter squash, yams.",
    passUp: "—",
  },
  {
    category: 'Fruits',
    categoryNote: 'Limit fruit to 2–3 servings per day',
    goAhead: "Apricot, bananas, berries of all types*, cantaloupe, casaba melon, cherries, coconut*, figs, grapefruit, grapes, guava, green apples*, honeydew melon, kiwi, kumquat, lemon, lime, mango, melon, nectarine, orange, papaya, passion fruit, peaches, pears*, persimmon, pineapple, plums, pomegranate, raisin, red apples.",
    goAheadNote: "* indicates fruits lower in sugar — preferable for those seeking fat loss.",
    passUp: "Canned fruits.",
  },
];

// Special groups (no Go/Pass split)
const foodSpecialGroups = [
  {
    category: 'Sugars, Flour Products, Grains & Beans',
    body: "Avoid them all as best as possible. If you are having a tough time adjusting, small ⅓-cup servings of cooked beans, rice, buckwheat, quinoa, millet, corn, and gluten-free oats are acceptable. Coconut and almond flours are also acceptable.",
    note: "Stevia, xylitol, and unheated, unfiltered honey are acceptable in moderation.",
  },
  {
    category: 'Beverages',
    body: "Consume about half your body weight in ounces of filtered water daily, preferably between meals. For variety, have coconut water, herbal tea, and/or fresh vegetable or fruit juice.",
  },
];

// Program days structure
const programDays = [
  {
    id: 1,
    title: 'Introduction',
    subtitle: 'Getting the C.R.A.P. Out',
    description: '',
    videoUrl: '',
    completed: false,
    tier: 'intro',
  },
  {
    id: 17,
    title: 'Food Charts',
    subtitle: 'Go Ahead · Pass It Up',
    description: '',
    videoUrl: '',
    completed: false,
    tier: 'intro',
  },
  {
    id: 2,
    title: 'Progress Chart',
    subtitle: 'Track Your Transformation',
    description: '',
    videoUrl: '',
    completed: false,
    tier: 'intro',
  },
  ...Array.from({ length: 14 }, (_, i) => ({
    id: i + 3,
    title: `Day ${i + 1}`,
    subtitle: '',
    description: '',
    videoUrl: '',
    completed: false,
    tier: 'full',
  })),
];

const sections = [
  { id: 'intro', title: 'Getting Started', days: programDays.slice(0, 3) },
  { id: 'week1', title: 'Week 1 (Days 1-7)', days: programDays.slice(3, 10) },
  { id: 'week2', title: 'Week 2 (Days 8-14)', days: programDays.slice(10, 17) },
];

const CrapCleansePage = () => {
  const { user, hasAccess } = useAuth();
  const { trackView, trackVideoPlay, trackLinkClick, trackComplete, trackSectionClick } = useActivityTracker('crap-cleanse');
  const [selectedDay, setSelectedDay] = useState(null);
  const [expandedSection, setExpandedSection] = useState('all');
  const [scores, setScores] = useState({});
  const [savedScores, setSavedScores] = useState(null);
  const [savingScores, setSavingScores] = useState(false);
  const [scoresSaved, setScoresSaved] = useState(false);
  const [cravings, setCravings] = useState({});

  const hasIntroAccess = hasAccess('crap-cleanse-intro') || hasAccess('crap-cleanse');
  const hasFullAccess = hasAccess('crap-cleanse');

  const isDayUnlocked = (day) => {
    if (day.tier === 'intro') return hasIntroAccess;
    return hasFullAccess;
  };

  // Load saved scores
  useEffect(() => {
    if (user?.id) {
      fetch(`${BACKEND_URL}/api/user/progress-chart/${user.id}`)
        .then(res => res.ok ? res.json() : null)
        .then(data => {
          if (data?.scores) {
            setSavedScores(data.scores);
            setScores(data.scores);
          }
          if (data?.cravings) {
            setCravings(data.cravings);
          }
        })
        .catch(() => {});
    }
  }, [user]);

  const handleScoreChange = (symptom, week, value) => {
    setScores(prev => ({
      ...prev,
      [`${symptom}-${week}`]: parseInt(value),
    }));
    setScoresSaved(false);
  };

  const handleCravingToggle = (food, week) => {
    setCravings(prev => {
      const k = `${food}-${week}`;
      const next = { ...prev };
      if (next[k]) {
        delete next[k];
      } else {
        next[k] = true;
      }
      return next;
    });
    setScoresSaved(false);
  };

  const saveScores = async () => {
    if (!user?.id) return;
    setSavingScores(true);
    try {
      await fetch(`${BACKEND_URL}/api/user/progress-chart`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: user.id,
          user_email: user.email,
          program_id: 'crap-cleanse',
          scores,
          cravings,
        }),
      });
      setSavedScores(scores);
      setScoresSaved(true);
      setTimeout(() => setScoresSaved(false), 3000);
    } catch (err) {
      console.error('Failed to save scores:', err);
    }
    setSavingScores(false);
  };

  const handleSelectDay = (day) => {
    if (!isDayUnlocked(day)) return;
    setSelectedDay(day);
    trackView(day.id, day.title + (day.subtitle ? ` - ${day.subtitle}` : ''));
  };

  if (!user) {
    return (
      <div className="bg-cream min-h-screen pt-24">
        <div className="max-w-4xl mx-auto px-6 md:px-12 py-16">
          <Link to="/programs" className="inline-flex items-center gap-2 text-forest/70 hover:text-forest transition-colors mb-8">
            <ArrowLeft size={18} />
            <span className="font-body text-sm">Back to Programs</span>
          </Link>
          <div className="text-center py-20">
            <Lock size={48} className="text-stone-300 mx-auto mb-4" />
            <h2 className="font-heading text-2xl text-forest mb-3">Sign In Required</h2>
            <p className="text-stone-500 font-body">Please sign in to access the 14-Day C.R.A.P. Cleanse.</p>
          </div>
        </div>
      </div>
    );
  }

  if (!hasIntroAccess && !hasFullAccess) {
    return (
      <div className="bg-cream min-h-screen pt-24">
        <div className="max-w-4xl mx-auto px-6 md:px-12 py-16">
          <Link to="/programs" className="inline-flex items-center gap-2 text-forest/70 hover:text-forest transition-colors mb-8">
            <ArrowLeft size={18} />
            <span className="font-body text-sm">Back to Programs</span>
          </Link>
          <div className="text-center py-20">
            <Lock size={48} className="text-stone-300 mx-auto mb-4" />
            <h2 className="font-heading text-2xl text-forest mb-3">Program Locked</h2>
            <p className="text-stone-500 font-body mb-6">Contact Jill to unlock access to the 14-Day C.R.A.P. Cleanse.</p>
            <Link to="/contact" className="bg-terracotta text-cream px-6 py-3 rounded-full font-body font-medium hover:bg-terracotta-dark transition-colors">
              Get in Touch
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-cream min-h-screen pt-24" data-testid="crap-cleanse-page">
      {/* Header */}
      <section className="py-8 md:py-12 bg-gradient-to-b from-forest/5 to-transparent">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Link to="/programs" className="inline-flex items-center gap-2 text-forest/70 hover:text-forest transition-colors mb-6">
            <ArrowLeft size={18} />
            <span className="font-body text-sm">Back to Programs</span>
          </Link>
          <div className="flex items-center gap-4 mb-2">
            <div className="w-12 h-12 bg-amber-500 rounded-xl flex items-center justify-center">
              <Apple size={24} className="text-white" />
            </div>
            <div>
              <h1 className="font-heading text-3xl md:text-4xl text-forest">14-Day C.R.A.P. Cleanse</h1>
              <p className="text-stone-500 font-body text-sm">Getting the C.R.A.P. out of your life</p>
            </div>
          </div>
          {hasIntroAccess && !hasFullAccess && (
            <div className="mt-4 bg-amber-50 border border-amber-200 rounded-xl p-3 inline-flex items-center gap-2">
              <Lock size={14} className="text-amber-600" />
              <span className="text-amber-700 font-body text-sm">Intro access only — daily lessons require full program access</span>
            </div>
          )}
        </div>
      </section>

      {/* Main Content */}
      <section className="py-8">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-2xl shadow-sm overflow-hidden sticky top-28">
                <div className="p-4 bg-forest text-cream">
                  <h3 className="font-heading text-lg">Program Outline</h3>
                  <p className="font-body text-xs text-cream/70 mt-1">14 days · Real food · Real results</p>
                </div>

                <div className="max-h-[60vh] overflow-y-auto">
                  {sections.map((section) => (
                    <div key={section.id}>
                      <button
                        onClick={() => setExpandedSection(expandedSection === section.id ? 'all' : section.id)}
                        className="w-full px-4 py-3 flex items-center justify-between bg-stone-50 border-b border-stone-100 hover:bg-stone-100 transition-colors"
                      >
                        <span className="font-body text-sm font-medium text-forest">{section.title}</span>
                        <ChevronDown
                          size={16}
                          className={`text-stone-400 transition-transform ${
                            expandedSection === 'all' || expandedSection === section.id ? 'rotate-180' : ''
                          }`}
                        />
                      </button>
                      <AnimatePresence>
                        {(expandedSection === 'all' || expandedSection === section.id) && (
                          <motion.div
                            initial={{ height: 0 }}
                            animate={{ height: 'auto' }}
                            exit={{ height: 0 }}
                            className="overflow-hidden"
                          >
                            {section.days.map((day) => {
                              const unlocked = isDayUnlocked(day);
                              return (
                                <button
                                  key={day.id}
                                  onClick={() => handleSelectDay(day)}
                                  className={`w-full px-4 py-2.5 flex items-center gap-3 border-b border-stone-50 transition-colors text-left ${
                                    selectedDay?.id === day.id ? 'bg-terracotta/10 border-l-4 border-l-terracotta' : ''
                                  } ${unlocked ? 'hover:bg-stone-50 cursor-pointer' : 'opacity-50 cursor-not-allowed'}`}
                                  data-testid={`day-${day.id}`}
                                >
                                  {unlocked ? (
                                    <div className="w-6 h-6 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                                      <Check size={12} className="text-amber-600" />
                                    </div>
                                  ) : (
                                    <Lock size={14} className="text-stone-300 flex-shrink-0" />
                                  )}
                                  <div className="flex-1 min-w-0">
                                    <p className={`font-body text-sm ${unlocked ? 'text-forest' : 'text-stone-400'}`}>
                                      {day.title}
                                    </p>
                                    {day.subtitle && (
                                      <p className="font-body text-xs text-stone-400 truncate">{day.subtitle}</p>
                                    )}
                                  </div>
                                </button>
                              );
                            })}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Main Content Area */}
            <div className="lg:col-span-2">
              {/* Welcome Screen */}
              {!selectedDay && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-white rounded-2xl shadow-sm overflow-hidden">
                  <div className="bg-gradient-to-br from-amber-500 to-orange-600 p-8 text-white">
                    <h2 className="font-heading text-3xl mb-3">Welcome to the 14-Day C.R.A.P. Cleanse</h2>
                    <p className="text-white/80 font-body">This Cleanse is different. Simply said — we are going to get the CRAP out!</p>
                  </div>
                  <div className="p-6">
                    <p className="text-stone-600 font-body leading-relaxed mb-4">
                      C.R.A.P. stands for <strong>C</strong>arbohydrate Rich, <strong>R</strong>efined, <strong>A</strong>rtificial, <strong>P</strong>rocessed foods. This is a no fad, nothing extreme, no starving, no potions or lotions, or anything special involved at all. Instead, we are going to eat <em>pure and simple</em>.
                    </p>
                    <p className="text-forest font-body font-medium">2 weeks to feel a difference.</p>
                    <div className="mt-6">
                      <button
                        onClick={() => handleSelectDay(programDays[0])}
                        className="bg-amber-500 text-white px-6 py-3 rounded-full font-body font-medium hover:bg-amber-600 transition-colors flex items-center gap-2"
                        data-testid="start-program-btn"
                      >
                        Get Started <ChevronRight size={18} />
                      </button>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Selected Day Content */}
              {selectedDay && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-white rounded-2xl shadow-sm overflow-hidden">
                  {/* Day Header */}
                  <div className="bg-forest p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <button onClick={() => setSelectedDay(null)} className="text-cream/70 hover:text-cream">
                        <ArrowLeft size={20} />
                      </button>
                      <div>
                        <h2 className="font-heading text-xl text-cream">{selectedDay.title}</h2>
                        {selectedDay.subtitle && (
                          <p className="text-cream/60 font-body text-sm">{selectedDay.subtitle}</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Introduction Content (id 1) */}
                  {selectedDay.id === 1 && (
                    <div className="p-6 md:p-8">
                      {/* Header */}
                      <div className="text-center mb-10">
                        <h3 className="font-heading text-3xl text-forest mb-3">Getting the C.R.A.P. Out</h3>
                        <p className="text-terracotta font-body text-lg italic mb-4">This Cleanse is different.</p>
                        <p className="text-stone-600 font-body leading-relaxed max-w-2xl mx-auto">
                          C.R.A.P. — <strong>C</strong>arbohydrate Rich, <strong>R</strong>efined, <strong>A</strong>rtificial, <strong>P</strong>rocessed foods. No fad, nothing extreme, no starving, no potions or lotions. Instead, we are going to eat <em>pure and simple</em>. 2 weeks to feel a difference.
                        </p>
                      </div>

                      {/* Does Cleansing Really Work? */}
                      <div className="mb-8 bg-white rounded-2xl border border-stone-200 overflow-hidden">
                        <div className="bg-emerald-50 px-6 py-4 border-b border-stone-200">
                          <h4 className="font-heading text-xl text-forest">Does Cleansing Really Work?</h4>
                        </div>
                        <div className="p-6">
                          <p className="text-stone-600 font-body leading-relaxed mb-4">
                            While it's true that our bodies are great at detoxing on their own, our environment and food system is overloaded with toxic and synthetic chemicals, making it tough for our systems to keep up.
                          </p>
                          <div className="bg-terracotta/10 rounded-xl p-4">
                            <p className="text-forest font-body italic text-sm">
                              By clearing your environment from chemicals, you reduce the unnecessary stress and load on your body so it has the time and space to heal itself.
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* The C.R.A.P. Breakdown */}
                      <div className="mb-8">
                        <h4 className="font-heading text-2xl text-forest text-center mb-6">What is C.R.A.P.?</h4>
                        <div className="space-y-4">
                          {/* C */}
                          <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
                            <div className="bg-red-50 px-6 py-4 border-b border-stone-200 flex items-center gap-3">
                              <div className="w-10 h-10 bg-red-500 rounded-full flex items-center justify-center text-white font-heading text-xl">C</div>
                              <h5 className="font-heading text-lg text-forest">Carbohydrate Rich</h5>
                            </div>
                            <div className="p-6">
                              <p className="text-stone-600 font-body text-sm leading-relaxed mb-4">
                                Not all carbs are bad — they're the fuel of the body! But there are two types: <strong>Complex carbs</strong> (fiber + sugar together, slow release) and <strong>Simple carbs</strong> (extracted sugar, quick spikes). We're eliminating the carbohydrate-rich simple carbs.
                              </p>
                              <div className="grid md:grid-cols-2 gap-3">
                                <div className="bg-emerald-50 rounded-lg p-3 border border-emerald-200">
                                  <p className="font-body font-medium text-emerald-700 text-sm mb-2">Complex Carbs (Good)</p>
                                  <ul className="space-y-1 text-stone-600 font-body text-xs">
                                    <li>Green & crunchy vegetables</li>
                                    <li>Whole grains, oatmeal</li>
                                    <li>Sweet potatoes, pumpkin</li>
                                    <li>Beans, lentils, peas</li>
                                  </ul>
                                </div>
                                <div className="bg-red-50 rounded-lg p-3 border border-red-200">
                                  <p className="font-body font-medium text-red-700 text-sm mb-2">Simple Carbs (Eliminate)</p>
                                  <ul className="space-y-1 text-stone-600 font-body text-xs">
                                    <li>Table & brown sugar</li>
                                    <li>Corn syrup, honey, maple syrup</li>
                                    <li>Soft drinks, fruit drinks</li>
                                    <li>Candy, jams, jellies</li>
                                  </ul>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* R */}
                          <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
                            <div className="bg-orange-50 px-6 py-4 border-b border-stone-200 flex items-center gap-3">
                              <div className="w-10 h-10 bg-orange-500 rounded-full flex items-center justify-center text-white font-heading text-xl">R</div>
                              <h5 className="font-heading text-lg text-forest">Refined</h5>
                            </div>
                            <div className="p-6">
                              <p className="text-stone-600 font-body text-sm leading-relaxed">
                                Refining removes impurities — but in food, it also removes all the nutrients, fiber, and enzymes that life, bodies, and minds are built upon. Flour, sugar, protein powders, chicken fingers, pizza — almost everything from a bag, box, can, or container has been refined. Empty calories, devoid of all nutrients.
                              </p>
                            </div>
                          </div>

                          {/* A */}
                          <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
                            <div className="bg-purple-50 px-6 py-4 border-b border-stone-200 flex items-center gap-3">
                              <div className="w-10 h-10 bg-purple-500 rounded-full flex items-center justify-center text-white font-heading text-xl">A</div>
                              <h5 className="font-heading text-lg text-forest">Artificial</h5>
                            </div>
                            <div className="p-6">
                              <p className="text-stone-600 font-body text-sm leading-relaxed mb-4">
                                Why would we eat artificial anything when the real thing is available, and tastes way better? Here's what artificial ingredients do in your food:
                              </p>
                              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                                {[
                                  { label: 'Color', desc: 'Neon glow in sodas' },
                                  { label: 'Stabilize', desc: 'Thickens soups & sauces' },
                                  { label: 'Emulsify', desc: 'Margarine, dressings' },
                                  { label: 'Bleach', desc: 'Flour, skim milk' },
                                  { label: 'Preserve', desc: 'Shelf life for months' },
                                  { label: 'Sweeten', desc: 'HFCS, aspartame' },
                                  { label: 'Flavor', desc: 'Fake watermelon year-round' },
                                  { label: 'Texturize', desc: 'Crispy cereal that stays crispy' },
                                  { label: 'Hide Odors', desc: 'Masks what food really is' },
                                ].map((item) => (
                                  <div key={item.label} className="bg-stone-50 rounded-lg p-2 text-center">
                                    <p className="font-body font-medium text-forest text-xs">{item.label}</p>
                                    <p className="font-body text-stone-500 text-xs">{item.desc}</p>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>

                          {/* P */}
                          <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
                            <div className="bg-amber-50 px-6 py-4 border-b border-stone-200 flex items-center gap-3">
                              <div className="w-10 h-10 bg-amber-500 rounded-full flex items-center justify-center text-white font-heading text-xl">P</div>
                              <h5 className="font-heading text-lg text-forest">Processed</h5>
                            </div>
                            <div className="p-6">
                              <p className="text-stone-600 font-body text-sm leading-relaxed">
                                Every step that food goes through from ground to table is a process. Each step removes life-giving elements, energy, nutrients, enzymes, and deliciousness. Picking, cutting, cooking, pasteurizing, deodorizing, canning, freezing, bottling — can you imagine how many nutrient-depriving steps take place? You can't get healthy from sick food. For 2 weeks, fill your body with real, life-giving foods — and feel the difference!
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Why is C.R.A.P. so bad? */}
                      <div className="mb-8 bg-white rounded-2xl border border-stone-200 overflow-hidden">
                        <div className="bg-red-50 px-6 py-4 border-b border-stone-200">
                          <h4 className="font-heading text-xl text-forest">Why is C.R.A.P. So Bad?</h4>
                        </div>
                        <div className="p-6">
                          <p className="text-stone-600 font-body text-sm mb-4">C.R.A.P. food can contribute to some or all of:</p>
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                            {[
                              'Weight gain', 'Difficulty losing weight', 'Digestive issues',
                              'Slowed metabolism', 'Muscular tension', 'Immunity issues',
                              'Fat stores around mid-section', 'Low energy levels', 'Chronic headaches',
                              'Insomnia', 'Hormonal imbalances', 'Skin conditions',
                              'Yeast & mold infections', 'Bloating & bad breath',
                            ].map((item) => (
                              <div key={item} className="bg-red-50 rounded-lg p-2 border border-red-100">
                                <p className="text-stone-600 font-body text-xs">{item}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* Benefits */}
                      <div className="mb-8 bg-white rounded-2xl border border-stone-200 overflow-hidden">
                        <div className="bg-emerald-50 px-6 py-4 border-b border-stone-200">
                          <h4 className="font-heading text-xl text-forest">Benefits of Getting the C.R.A.P. Out</h4>
                        </div>
                        <div className="p-6">
                          <div className="space-y-3">
                            {[
                              { title: 'Beats the bloat', desc: '' },
                              { title: 'Reignites your energy', desc: 'Without feeling laden down by toxins' },
                              { title: 'Reduces fat stores', desc: 'Fat is the body\'s cleverest way to store toxins' },
                              { title: 'Restores health & immunity', desc: '' },
                              { title: 'Regulates blood sugar & metabolism', desc: 'So you burn more calories at complete rest' },
                              { title: 'Kicks food addictions', desc: 'Re-energizing taste buds and clearing addictive additives' },
                              { title: 'Rests vital organs', desc: 'Especially the liver, from working overtime' },
                            ].map((item, idx) => (
                              <div key={idx} className="flex gap-3">
                                <div className="w-6 h-6 bg-emerald-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                                  <Check size={12} className="text-white" />
                                </div>
                                <div>
                                  <span className="font-body font-medium text-forest text-sm">{item.title}</span>
                                  {item.desc && <span className="text-stone-500 font-body text-sm"> — {item.desc}</span>}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* Food Lists */}
                      <div className="mb-8">
                        <h4 className="font-heading text-2xl text-forest text-center mb-6">What We're Going to Eat</h4>

                        {/* Go Ahead */}
                        <div className="mb-4 bg-white rounded-2xl border-2 border-emerald-200 overflow-hidden">
                          <div className="bg-emerald-500 px-6 py-4">
                            <h5 className="font-heading text-lg text-white">Go Ahead & Enjoy Fully</h5>
                            <p className="text-emerald-100 font-body text-sm">Real foods! 90% of the time</p>
                          </div>
                          <div className="p-6">
                            <div className="grid md:grid-cols-2 gap-4">
                              <div>
                                <p className="font-body font-medium text-forest text-sm mb-2">Vegetables</p>
                                <p className="text-stone-500 font-body text-xs">Any vegetable you can think of. Local and organic is best, in season is also best. Eat at least double the amount of vegetables to fruits.</p>
                              </div>
                              <div>
                                <p className="font-body font-medium text-forest text-sm mb-2">Fruits</p>
                                <p className="text-stone-500 font-body text-xs">Any fruit you can think of. Eat at least half the amount of fruit as vegetables.</p>
                              </div>
                              <div>
                                <p className="font-body font-medium text-forest text-sm mb-2">Proteins</p>
                                <p className="text-stone-500 font-body text-xs">Any meat, poultry, fish, shellfish — in purest form, as few steps from land to table. Leaner cuts, preferably unfrozen. Limit red meats to 2x per week. Wild fish, no farmed.</p>
                              </div>
                              <div>
                                <p className="font-body font-medium text-forest text-sm mb-2">Fats</p>
                                <p className="text-stone-500 font-body text-xs">Coconut oil (unrefined, extra virgin), olive oil (extra virgin), butter or ghee (organic). The purest, most unrefined as possible.</p>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Proceed with Caution */}
                        <div className="mb-4 bg-white rounded-2xl border-2 border-amber-200 overflow-hidden">
                          <div className="bg-amber-500 px-6 py-4">
                            <h5 className="font-heading text-lg text-white">Keep It to a Minimum</h5>
                            <p className="text-amber-100 font-body text-sm">Proceed with caution</p>
                          </div>
                          <div className="p-6">
                            <div className="space-y-3">
                              <div>
                                <p className="font-body font-medium text-forest text-sm">Starches & Grains</p>
                                <p className="text-stone-500 font-body text-xs">All grains come from a box or container, meaning some processing. Keep it minimal. Brown, red, wild rice are better options.</p>
                              </div>
                              <div>
                                <p className="font-body font-medium text-forest text-sm">Fats to Limit</p>
                                <p className="text-stone-500 font-body text-xs">Canola oils, commercial dressings, margarine, lard, palm oils, vegetable oils — these should be strictly, if not completely, eliminated.</p>
                              </div>
                              <div>
                                <p className="font-body font-medium text-forest text-sm">Dairy & Alternatives</p>
                                <p className="text-stone-500 font-body text-xs">Consume sparingly. Dairy has sensitivity issues; alternatives often contain gums and carrageenan with links to health issues.</p>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Avoid */}
                        <div className="bg-white rounded-2xl border-2 border-red-200 overflow-hidden">
                          <div className="bg-red-500 px-6 py-4">
                            <h5 className="font-heading text-lg text-white">Avoid Like Your Life Depends On It</h5>
                            <p className="text-red-100 font-body text-sm">Turn around, run away — eat REAL foods instead</p>
                          </div>
                          <div className="p-6">
                            <p className="text-stone-600 font-body text-sm mb-3">
                              Anything and everything from a bag, bottle, box, can, carton, container, package, foil, plastic, anything with a zip seal, anything that pops when you open it — avoid it!
                            </p>
                            <div className="bg-stone-50 rounded-lg p-4">
                              <p className="text-forest font-body text-sm font-medium mb-2">The 90/10 Rule</p>
                              <p className="text-stone-500 font-body text-xs">
                                90% of the time fresh, whole foods. 10% will likely come from some form of package. If you slip, stick to gluten-free options. This is stronger than the 80/20 maintenance rule — we need 90/10 to shake things up.
                              </p>
                            </div>
                            <div className="mt-3 bg-terracotta/10 rounded-lg p-4 text-center">
                              <p className="text-forest font-heading text-lg">YOU CAN DO THIS!</p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Closing */}
                      <div className="text-center py-4">
                        <p className="font-heading text-2xl text-forest mb-2">For the next 14 days — just eat real food.</p>
                        <p className="text-stone-500 font-body">As much as possible, wherever and whenever possible.</p>
                      </div>
                    </div>
                  )}

                  {/* Progress Chart (id 2) */}
                  {selectedDay.id === 2 && (
                    <div className="p-6 md:p-8">
                      <div className="text-center mb-8">
                        <h3 className="font-heading text-3xl text-forest mb-3">Progress Chart</h3>
                        <p className="text-stone-500 font-body">Score each symptom from 1-5 at the beginning and end of each week</p>
                        <div className="flex flex-wrap justify-center gap-3 mt-4">
                          <span className="bg-red-100 text-red-700 px-3 py-1 rounded-full text-xs font-body">1 = Very frequent/severe</span>
                          <span className="bg-orange-100 text-orange-700 px-3 py-1 rounded-full text-xs font-body">2 = Regular/bad</span>
                          <span className="bg-amber-100 text-amber-700 px-3 py-1 rounded-full text-xs font-body">3 = Fairly regular/tolerable</span>
                          <span className="bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full text-xs font-body">4 = Infrequent/manageable</span>
                          <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-xs font-body">5 = Rare/minor</span>
                        </div>
                      </div>

                      <div className="overflow-x-auto">
                        <table className="w-full border-collapse" data-testid="progress-chart">
                          <thead>
                            <tr className="bg-forest text-cream">
                              <th className="px-4 py-3 text-left font-heading text-sm rounded-tl-lg">Symptom</th>
                              <th className="px-3 py-3 text-center font-heading text-sm">Start</th>
                              <th className="px-3 py-3 text-center font-heading text-sm">Week 1</th>
                              <th className="px-3 py-3 text-center font-heading text-sm rounded-tr-lg">Week 2</th>
                            </tr>
                          </thead>
                          <tbody>
                            {symptomCategories.map((symptom, idx) => (
                              <tr key={symptom} className={idx % 2 === 0 ? 'bg-white' : 'bg-stone-50'}>
                                <td className="px-4 py-2.5 font-body text-sm text-forest">{symptom}</td>
                                {['start', 'week1', 'week2'].map((week) => (
                                  <td key={week} className="px-3 py-2.5 text-center">
                                    <select
                                      value={scores[`${symptom}-${week}`] || ''}
                                      onChange={(e) => handleScoreChange(symptom, week, e.target.value)}
                                      className="w-14 px-2 py-1 rounded-lg border border-stone-200 text-sm font-body text-center bg-white focus:ring-2 focus:ring-amber-300 focus:border-amber-300"
                                      data-testid={`score-${symptom}-${week}`}
                                    >
                                      <option value="">-</option>
                                      <option value="1">1</option>
                                      <option value="2">2</option>
                                      <option value="3">3</option>
                                      <option value="4">4</option>
                                      <option value="5">5</option>
                                    </select>
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {/* Conquering Cravings Chart */}
                      <div className="mt-12 mb-2">
                        <div className="text-center mb-6">
                          <h3 className="font-heading text-3xl text-forest mb-3">Conquering Cravings!</h3>
                          <p className="text-stone-600 font-body max-w-2xl mx-auto leading-relaxed mb-3">
                            Tick the foods you most crave during the Cleanse. See for yourself how your body's demands change over the course of the 2 weeks.
                          </p>
                          <p className="text-stone-500 font-body text-sm max-w-2xl mx-auto leading-relaxed italic">
                            Complete this chart at the end of each cleanse week to gauge how effective you are at combating your cravings. You'll notice the foods you crave in Week 1 are different from those you may crave in Weeks 2 and 3 if you choose to carry on longer. This is because you are effectively retraining and re-educating your palate to reject extreme tastes of the very sugary and very salty.
                          </p>
                        </div>

                        <div className="overflow-x-auto">
                          <table className="w-full border-collapse" data-testid="cravings-chart">
                            <thead>
                              <tr className="bg-forest text-cream">
                                <th className="px-4 py-3 text-left font-heading text-sm rounded-tl-lg">Food / Craving</th>
                                {cravingWeeks.map((w, i) => (
                                  <th
                                    key={w.key}
                                    className={`px-3 py-3 text-center font-heading text-xs sm:text-sm ${i === cravingWeeks.length - 1 ? 'rounded-tr-lg' : ''}`}
                                  >
                                    {w.label}
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {cravingItems.map((food, idx) => (
                                <tr key={food} className={idx % 2 === 0 ? 'bg-white' : 'bg-stone-50'}>
                                  <td className="px-4 py-2.5 font-body text-sm text-forest">{food}</td>
                                  {cravingWeeks.map((w) => {
                                    const k = `${food}-${w.key}`;
                                    const isChecked = !!cravings[k];
                                    return (
                                      <td key={w.key} className="px-3 py-2.5 text-center">
                                        <button
                                          type="button"
                                          onClick={() => handleCravingToggle(food, w.key)}
                                          className={`w-7 h-7 rounded-md border-2 flex items-center justify-center transition-all mx-auto ${
                                            isChecked
                                              ? 'bg-terracotta border-terracotta text-white'
                                              : 'bg-white border-stone-300 hover:border-terracotta'
                                          }`}
                                          aria-pressed={isChecked}
                                          aria-label={`${food} — ${w.label}`}
                                          data-testid={`craving-${food.replace(/\s+/g, '-').toLowerCase()}-${w.key}`}
                                        >
                                          {isChecked && <Check size={14} strokeWidth={3} />}
                                        </button>
                                      </td>
                                    );
                                  })}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>

                      <div className="mt-6 flex items-center justify-between">
                        <button
                          onClick={saveScores}
                          disabled={savingScores}
                          className="bg-forest text-cream px-6 py-3 rounded-full font-body font-medium hover:bg-forest-dark transition-colors disabled:opacity-50 flex items-center gap-2"
                          data-testid="save-scores-btn"
                        >
                          {savingScores ? 'Saving...' : scoresSaved ? (
                            <><Check size={16} /> Saved!</>
                          ) : 'Save My Progress'}
                        </button>
                        {savedScores && (
                          <span className="text-stone-400 font-body text-sm">Last saved progress loaded</span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Food Charts (id 17) */}
                  {selectedDay.id === 17 && (
                    <div className="p-6 md:p-8">
                      <div className="text-center mb-8">
                        <h3 className="font-heading text-3xl text-forest mb-3">Food Charts</h3>
                        <p className="text-stone-500 font-body max-w-2xl mx-auto">A quick reference of what to lean into and what to set aside during the cleanse. Group-by-group.</p>
                        <div className="flex flex-wrap justify-center gap-3 mt-4">
                          <span className="bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full text-xs font-body">Go Ahead</span>
                          <span className="bg-rose-100 text-rose-700 px-3 py-1 rounded-full text-xs font-body">Pass It Up!</span>
                        </div>
                      </div>

                      <div className="space-y-5" data-testid="food-charts">
                        {foodChartGroups.map((g) => (
                          <div key={g.category} className="rounded-xl border border-stone-200 overflow-hidden bg-white shadow-sm">
                            <div className="bg-stone-100 px-5 py-3 border-b border-stone-200">
                              <h4 className="font-heading text-lg text-forest">{g.category}</h4>
                              {g.categoryNote && (
                                <p className="text-stone-500 font-body text-xs italic mt-0.5">{g.categoryNote}</p>
                              )}
                            </div>
                            <div className="grid md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-stone-200">
                              <div className="bg-emerald-50/50 p-5">
                                <p className="font-body text-xs uppercase tracking-[0.18em] text-emerald-700 font-semibold mb-2">Go Ahead</p>
                                <p className="font-body text-sm text-stone-700 leading-relaxed whitespace-pre-line">{g.goAhead}</p>
                                {g.goAheadNote && (
                                  <p className="font-body text-xs text-stone-500 italic mt-2 leading-relaxed">{g.goAheadNote}</p>
                                )}
                              </div>
                              <div className="bg-rose-50/50 p-5">
                                <p className="font-body text-xs uppercase tracking-[0.18em] text-rose-700 font-semibold mb-2">Pass It Up</p>
                                <p className="font-body text-sm text-stone-700 leading-relaxed">{g.passUp}</p>
                                {g.passUpNote && (
                                  <p className="font-body text-xs text-stone-500 italic mt-2 leading-relaxed">{g.passUpNote}</p>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}

                        {foodSpecialGroups.map((g) => (
                          <div key={g.category} className="rounded-xl border border-stone-200 overflow-hidden bg-white shadow-sm">
                            <div className="bg-stone-100 px-5 py-3 border-b border-stone-200">
                              <h4 className="font-heading text-lg text-forest">{g.category}</h4>
                            </div>
                            <div className="p-5">
                              <p className="font-body text-sm text-stone-700 leading-relaxed">{g.body}</p>
                              {g.note && (
                                <p className="font-body text-xs text-forest italic mt-3 bg-forest/5 rounded-lg p-3">{g.note}</p>
                              )}
                            </div>
                          </div>
                        ))}

                        <div className="rounded-xl bg-amber-50 border border-amber-200 p-5">
                          <p className="font-body text-sm text-amber-900 leading-relaxed">
                            <span className="font-semibold">**All herbs and natural spices are allowed.</span>{' '}
                            Watch for combos and make sure no MSG's or fillers — plain ones are safe; otherwise read labels on combos.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Generic Day Content (Days 1-14) */}
                  {selectedDay.id >= 3 && selectedDay.id <= 16 && (
                    <div className="p-6 md:p-8">
                      <div className="text-center py-16">
                        <div className="w-20 h-20 bg-stone-100 rounded-full flex items-center justify-center mx-auto mb-4">
                          <Video size={32} className="text-stone-400" />
                        </div>
                        <h3 className="font-heading text-xl text-forest mb-2">{selectedDay.title}</h3>
                        <p className="text-stone-500 font-body">Content coming soon</p>
                      </div>
                    </div>
                  )}
                </motion.div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CrapCleansePage;
